import React, { useEffect, useState, useRef } from 'react';

import { useCurrentWidth } from '../../customer_service/getWidth.component';

export interface VideoModalProps {
    id: string;
    url: string;
    thumbnail: string;
    show: boolean;
    VideoCommonTitle: string;
    subtitle: string;
    onCloseClick: () => void;
}

const VideoModal = ({ url, show, thumbnail, VideoCommonTitle, subtitle, onCloseClick, id }: VideoModalProps) => {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const windowSize = useCurrentWidth();
    const [modalWidth, setModalWidth] = useState(0);
    const [modalHeight, setModalHeight] = useState(0);
    const [showVideo, setShowVideo] = useState(false);
    const [isVideoInitialized, setIsVideoInitialized] = useState(false);

    const calculateModalSize = () => {
        const windowAspect = windowSize.height / windowSize.width;
        const header = 19 + 9;
        const videoAspect = 9 / 16;
        let modalW: number, modalH: number;
        let heightLimit = 0;
        let widthLimit = 0;
        let videoAreaSize = 0;
        if (windowAspect > 1) {
            // portrait
            heightLimit = windowSize.height * 0.95;
            widthLimit = windowSize.width * 0.9;

            modalW = widthLimit;
            modalH = modalW * videoAspect + header;
        } else {
            // landscape
            heightLimit = windowSize.height * 0.95;
            widthLimit = windowSize.width * 0.9;
            videoAreaSize = heightLimit - header;
            modalW = videoAreaSize / videoAspect;
            modalH = heightLimit;

            if (modalW > widthLimit) {
                // console.log('%c width too large---------', 'color: red; font-weight: bold;');
                modalW = widthLimit;
                modalH = modalW * videoAspect + header;
            }
        }
        setModalWidth(modalW);
        setModalHeight(modalH);
    };

    useEffect(() => calculateModalSize(), [windowSize]);

    const trapFocusInModal = (event: KeyboardEvent) => {
        if (event.code === 'Escape') {
            event.preventDefault();
            onCloseClick();
            return;
        }
        const isTabPressed = event.key === 'Tab';
        if (!isTabPressed) {
            return;
        }
        const focusableElements = `
          button:not(.amp-waiting.amp-pause-overlay):not(.amp-playing.amp-pause-overlay):not(.amp-qualitychange-manual.amp-pause-overlay):not(.amp-busy.amp-pause-overlay):not(.amp-icon.amp-facebook):not(.amp-icon.amp-twitter):not(.amp-icon.amp-email):not(.amp-icon.amp-embed):not(.amp-icon.amp-icon-right.amp-right):not(.amp-right.amp-icon.amp-toggle),
          [href],
          input,
          select,
          textarea,
          span[tabindex]
      `;
        const overlay = overlayRef.current;
        const firstFocusableElement = overlay.querySelectorAll(focusableElements)[0] as HTMLElement;
        const focusableContent = overlay.querySelectorAll(focusableElements);

        if (!focusableContent) {
            return;
        }
        const lastFocusableElement = focusableContent[focusableContent.length - 1] as HTMLElement;
        if (event.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
                event.preventDefault();
                lastFocusableElement.focus();
            }
        } else {
            if (document.activeElement === lastFocusableElement) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        }
    };

    useEffect(() => {
        overlayRef.current.style.display = 'flex';
        document.addEventListener('keydown', trapFocusInModal);
        return () => document.removeEventListener('keydown', trapFocusInModal);
    }, []);

    const setUpVideo = () => {
        akamai.amp.AMP.create(`video-${id}`, {
            background: '#000',
            autoplay: true,
            autoplayPolicy: 'muted',
            temporalType: 'vod',
            media: {
                // poster: poster,
                src: url,
                type: 'video/mp4',
            },
            preload: 'auto',
            paths: {
                player: '/react/components/amp/',
                resources: '/react/components/amp/',
                plugins: '/react/components/amp/',
            },
        }).then(function (amp) {
            let percentageComplete = [];
            amp.addEventListener(akamai.amp.Events.PLAY_REQUEST, (event) => {
                //console.log('VIDEO START');
                window._satellite && window._satellite.track('VideoStart');
            });
            amp.addEventListener(akamai.amp.Events.PLAY, (event) => {
                //console.log('VIDEO PLAY');
                window._satellite && window._satellite.track('Play');
            });
            amp.addEventListener(akamai.amp.Events.ENDED, (event) => {
                //console.log('VIDEO 100');
                window._satellite && window._satellite.track('Video100Percent');
                percentageComplete = [];
            });
            amp.addEventListener(akamai.amp.Events.TIME_UPDATE, (event) => {
                const currentTime = Number(event.player.absoluteCurrentTime);
                const duration = Number(event.player.absoluteDuration);
                const percentViewed = Math.floor((currentTime / duration) * 100);
                //console.log(currentTime, ' / ', duration, ' - ', percentViewed);
                switch (true) {
                    case percentViewed == 0:
                        percentageComplete = [];
                        break;
                    case percentViewed == 25:
                        if (!(percentageComplete.indexOf('Video25Percent') > -1)) {
                            //console.log('VIDEO: tracking 25% MILESTONE event');
                            window._satellite && window._satellite.track('Video25Percent');
                            percentageComplete.push('Video25Percent');
                        }
                        break;
                    case percentViewed == 50:
                        if (!(percentageComplete.indexOf('Video50Percent') > -1)) {
                            //console.log('VIDEO: tracking 50% MILESTONE event');
                            window._satellite && window._satellite.track('Video50Percent');
                            percentageComplete.push('Video50Percent');
                        }
                        break;
                    case percentViewed == 75:
                        if (!(percentageComplete.indexOf('Video75Percent') > -1)) {
                            //console.log('VIDEO: tracking 75% MILESTONE event');
                            window._satellite && window._satellite.track('Video75Percent');
                            percentageComplete.push('Video75Percent');
                        }
                        break;
                }
            });
        });
    };

    useEffect(() => {
        if (url) setShowVideo(true);
    }, []);

    useEffect(() => {
        if (!isVideoInitialized) {
            if (showVideo) {
                setUpVideo();
                setIsVideoInitialized(true);
            }
        }
    }, [showVideo]);

    return (
        <div
            className="videoHeroInPage__overlay"
            id={`customer-service-faq-video-modal-dialog-${id}`}
            ref={overlayRef}
            role="dialog"
        >
            <div style={{ width: modalWidth, height: modalHeight }}>
                <span
                    className="videoHeroInPage__overlay-title"
                    id={`customer-service-faq-video-modal-heading-${id}`}
                    tabIndex={0}
                    role="heading"
                >
                    {subtitle}
                </span>
                {showVideo && (
                    <div className="customer-service-faq__video-modal__body">
                        <div className="customer-service-faq__video-modal__ratio">
                            <div
                                className="jwVidContainer customer-service-faq__video-modal__video"
                                id={`video-${id}`}
                                ref={videoContainerRef}
                            />
                        </div>
                    </div>
                )}
            </div>
            <button
                className="videoHeroInPage__close-button"
                onClick={(e) => {
                    e.preventDefault();
                    onCloseClick();
                }}
            >
                <span className="visually-hidden">Close dialog window</span>
            </button>
        </div>
    );
};

export default VideoModal;
